import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { QueryModule } from "@/data/queryModules";
import { AppQueryOptions, AppInfiniteQueryOptions } from "@povio/openapi-codegen-cli";
import { CommonModels } from "@/data/common/common.models";
import { PublicProjectsApi } from "./publicProjects.api";

export namespace PublicProjectsQueries {

export const moduleName = QueryModule.PublicProjects;

export const keys = {
    all: [moduleName] as const,
    getProjects: (userId: string, limit?: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, page?: number, cursor?: string, ) => [...keys.all, "/public/profile/:userId/projects", userId, limit, order, filter, page, cursor] as const,
    getProjectsInfinite: (userId: string, limit?: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, cursor?: string, ) => [...keys.all, "/public/profile/:userId/projects", "infinite", userId, limit, order, filter, cursor] as const,
    getFeaturedProject: (userId: string, ) => [...keys.all, "/public/profile/:userId/projects/featured", userId] as const,
    show: (userId: string, id: string, ) => [...keys.all, "/public/profile/:userId/projects/:id", userId, id] as const,
    getMedia: (userId: string, id: string, ) => [...keys.all, "/public/profile/:userId/projects/:id/media", userId, id] as const,
};


 /** 
 * Query `useGetProjects`
 * @summary Get projects
 * @param { string } object.userId Path parameter
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): title, description, featured, createdAt, updatedAt. Example: `title`
 * @param { CommonModels.ProjectPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<PublicProjectsModels.GetProjectsResponse> } 
 * @statusCodes [200]
 */
export const useGetProjects = <TData>({ userId, limit, order, filter, page, cursor }: { userId: string, limit: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, page?: number, cursor?: string,  }, options?: AppQueryOptions<typeof PublicProjectsApi.getProjects, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getProjects(userId, limit, order, filter, page, cursor),
    queryFn: () =>  
    
    PublicProjectsApi.getProjects(userId, limit, order, filter, page, cursor)
    ,
    ...options,
  });
};



/** 
 * Infinite query `useGetProjectsInfinite
 * @summary Get projects
 * @param { string } object.userId Path parameter
 * @param { number } object.limit Query parameter. Items per response. Minimum: `1`. Maximum: `100`. Default: `10`
 * @param { string } object.order Query parameter. Order by fields (comma separated with +/- prefix): title, description, featured, createdAt, updatedAt. Example: `title`
 * @param { CommonModels.ProjectPaginationFilterDto } object.filter Query parameter
 * @param { number } object.page Query parameter. 1-indexed page number to begin from
 * @param { string } object.cursor Query parameter. ID of item to start after
 * @param { AppInfiniteQueryOptions } options Infinite query options
 * @returns { UseInfiniteQueryResult<PublicProjectsModels.GetProjectsResponse> } 
 * @statusCodes [200]
 */
export const useGetProjectsInfinite = <TData>({ userId, limit, order, filter, cursor }: { userId: string, limit: number, order?: string, filter?: CommonModels.ProjectPaginationFilterDto, cursor?: string,  }, options?: AppInfiniteQueryOptions<typeof PublicProjectsApi.getProjects, TData>) => {
  

  return useInfiniteQuery({
    queryKey: keys.getProjectsInfinite(userId, limit, order, filter, cursor),
    queryFn: ({ pageParam }) =>  
    
    PublicProjectsApi.getProjects(userId, limit, order, filter, pageParam, cursor)
    ,
    initialPageParam: 1,
    getNextPageParam: ({ page, totalItems, limit: limitParam }) => {
      const pageParam = page ?? 1;
      return pageParam * limitParam < totalItems ? pageParam + 1 : null;
    },
    ...options,
  });
};


 /** 
 * Query `useGetFeaturedProject`
 * @summary Get featured project
 * @param { string } object.userId Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProjectWithMediaDto> } FeaturedProject
 * @statusCodes [200]
 */
export const useGetFeaturedProject = <TData>({ userId }: { userId: string,  }, options?: AppQueryOptions<typeof PublicProjectsApi.getFeaturedProject, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getFeaturedProject(userId),
    queryFn: () =>  
    
    PublicProjectsApi.getFeaturedProject(userId)
    ,
    ...options,
  });
};






 /** 
 * Query `useShow`
 * @summary Get project by id
 * @param { string } object.userId Path parameter
 * @param { string } object.id Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProjectDto> } Project
 * @statusCodes [200]
 */
export const useShow = <TData>({ userId, id }: { userId: string, id: string,  }, options?: AppQueryOptions<typeof PublicProjectsApi.show, TData>) => {
  
  
  return useQuery({
    queryKey: keys.show(userId, id),
    queryFn: () =>  
    
    PublicProjectsApi.show(userId, id)
    ,
    ...options,
  });
};






 /** 
 * Query `useGetMedia`
 * @summary Get media for project
 * @param { string } object.userId Path parameter
 * @param { string } object.id Path parameter
 * @param { AppQueryOptions } options Query options
 * @returns { UseQueryResult<CommonModels.ProjectMediaDto> } ProjectMedia
 * @statusCodes [200]
 */
export const useGetMedia = <TData>({ userId, id }: { userId: string, id: string,  }, options?: AppQueryOptions<typeof PublicProjectsApi.getMedia, TData>) => {
  
  
  return useQuery({
    queryKey: keys.getMedia(userId, id),
    queryFn: () =>  
    
    PublicProjectsApi.getMedia(userId, id)
    ,
    ...options,
  });
};






}
